import React, { useState, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTRPC, useTRPCClient } from "@/trpc";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  FileText,
  Download,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Image as ImageIcon,
  Loader2,
  XCircle,
  CheckCircle2,
  Clock,
} from "lucide-react";
import PDFViewer from "@/components/PDFViewer/PDFViewer";
import ImageViewer from "@/components/MediaViewer/ImageViewer";

const FORM_RESPONSE_DIALOG_CLASS_NAME =
  "[&.dialog-content]:w-[calc(100vw-2rem)] [&.dialog-content]:max-w-3xl [&.dialog-content]:max-h-[calc(100dvh-2rem)] [&.dialog-content]:overflow-y-auto min-w-0 gap-4 rounded-2xl border-slate-200 bg-white p-4 text-slate-900 shadow-xl sm:rounded-2xl sm:p-6";

type PdfResult = {
  fillStatus: string;
  downloadUrl: string | null;
};

/** Banner showing the PDF fill status + download button for PDF form submissions. */
function PdfResultBanner({ pdfResult }: { pdfResult: PdfResult }) {
  if (pdfResult.fillStatus === "failed") {
    return (
      <div className="flex min-w-0 items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 [overflow-wrap:anywhere]">
        <XCircle size={16} className="text-red-500 mt-0.5 shrink-0" />
        <div>
          <p className="text-sm font-medium text-red-800">PDF fill failed</p>
          <p className="text-xs text-red-600 mt-0.5">
            The PDF could not be generated. Please contact support if this
            persists.
          </p>
        </div>
      </div>
    );
  }

  if (pdfResult.fillStatus === "filled" && pdfResult.downloadUrl) {
    const downloadUrl = pdfResult.downloadUrl;
    // The signed URL is cross-origin, so `a.download` is ignored and the
    // browser navigates to it. Fetch the bytes and download via a blob instead;
    // fall back to a new tab so we never navigate the current window away.
    const handleDownload = async () => {
      try {
        const res = await fetch(downloadUrl);
        if (!res.ok) {
          throw new Error("download failed");
        }
        const blob = await res.blob();
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = objectUrl;
        a.download = "completed-form.pdf";
        a.click();
        URL.revokeObjectURL(objectUrl);
      } catch {
        window.open(downloadUrl, "_blank", "noopener,noreferrer");
      }
    };

    return (
      <div className="min-w-0 rounded-2xl border border-slate-200 overflow-hidden">
        <div className="flex min-w-0 flex-wrap items-center gap-3 bg-green-50 px-4 py-3">
          <CheckCircle2 size={16} className="text-green-600 shrink-0" />
          <p className="min-w-0 flex-1 basis-28 text-sm font-medium text-green-800 [overflow-wrap:anywhere]">
            Completed PDF
          </p>
          <Button
            className="min-h-12 gap-2 shrink-0"
            onClick={() => void handleDownload()}
          >
            <Download size={16} className="shrink-0" />
            Download
          </Button>
        </div>
        <div className="min-w-0 border-t border-slate-200 bg-slate-50">
          <PDFViewer url={downloadUrl} containerClassName="h-[60vh]" />
        </div>
      </div>
    );
  }

  // pending or processing
  return (
    <div className="flex min-w-0 items-start gap-3 rounded-xl border border-primary/15 bg-primary/5 px-4 py-3">
      <Clock size={16} className="mt-0.5 text-primary shrink-0" />
      <div className="min-w-0 flex-1 [overflow-wrap:anywhere]">
        <p className="text-sm font-medium text-primary">PDF being generated</p>
        <p className="text-xs leading-5 text-slate-600 mt-0.5">
          This usually takes a few seconds. The page will update automatically.
        </p>
      </div>
      <Loader2
        size={14}
        className="mt-0.5 text-primary animate-spin shrink-0"
      />
    </div>
  );
}

interface FormResponseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  formSubmissionId: string;
}

interface FileAttachmentLinkProps {
  formSubmissionId: string;
  fileUpload: {
    id: string;
    fileName: string;
    mimeType: string;
    scanStatus: string;
  };
}

function isPreviewable(mimeType: string) {
  return mimeType === "application/pdf" || mimeType.startsWith("image/");
}

function FileAttachmentLink({
  formSubmissionId,
  fileUpload,
}: FileAttachmentLinkProps) {
  const trpcClient = useTRPCClient();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  const isQuarantined = fileUpload.scanStatus === "quarantined";
  const isClean = fileUpload.scanStatus === "clean";
  const canPreview = isClean && isPreviewable(fileUpload.mimeType);

  const getSignedUrl = useCallback(async () => {
    if (signedUrl) {
      return signedUrl;
    }
    setIsLoading(true);
    setError(null);
    try {
      // The API authorizes by submission and file id and signs the URL;
      // the browser never reaches Storage with the user's JWT.
      const { url } =
        await trpcClient.organizationForm.getSubmissionAttachmentUrl.query({
          formSubmissionId,
          fileUploadId: fileUpload.id,
        });
      setSignedUrl(url);
      return url;
    } catch {
      setError("Could not open file. Try again.");
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [trpcClient, formSubmissionId, fileUpload.id, signedUrl]);

  const handlePreviewToggle = useCallback(async () => {
    if (showPreview) {
      setShowPreview(false);
      return;
    }
    const url = await getSignedUrl();
    if (url) {
      setShowPreview(true);
    }
  }, [showPreview, getSignedUrl]);

  const handleDownload = useCallback(async () => {
    const url = await getSignedUrl();
    if (url) {
      const a = document.createElement("a");
      a.href = url;
      a.download = fileUpload.fileName;
      a.click();
    }
  }, [getSignedUrl, fileUpload.fileName]);

  const ext = fileUpload.fileName.split(".").pop()?.toUpperCase() ?? "FILE";
  const isPdf = fileUpload.mimeType === "application/pdf";
  const isImage = fileUpload.mimeType.startsWith("image/");

  if (isQuarantined) {
    return (
      <div className="flex min-w-0 items-start gap-3 p-3 rounded-xl border border-red-200 bg-red-50">
        <AlertTriangle size={14} className="text-red-500 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm text-red-700 font-medium [overflow-wrap:anywhere]">
            {fileUpload.fileName}
          </p>
          <p className="text-xs leading-5 text-red-600 [overflow-wrap:anywhere]">
            File flagged by security scan — unavailable
          </p>
        </div>
      </div>
    );
  }

  if (!isClean) {
    return (
      <div className="flex min-w-0 items-start gap-3 p-3 rounded-xl border border-slate-200 bg-slate-50">
        <FileText size={14} className="mt-0.5 text-slate-500 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm text-slate-600 [overflow-wrap:anywhere]">
            {fileUpload.fileName}
          </p>
          <p className="text-xs leading-5 text-slate-500 [overflow-wrap:anywhere]">
            Scan pending...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-w-0 max-w-full rounded-xl border border-slate-200 overflow-hidden">
      <div className="flex min-w-0 flex-wrap items-center gap-2 p-3 bg-white">
        <div className="flex min-w-0 flex-1 basis-48 items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10 shrink-0">
            {isImage ? (
              <ImageIcon size={14} className="text-primary" />
            ) : (
              <FileText size={14} className="text-primary" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-slate-900 font-medium [overflow-wrap:anywhere]">
              {fileUpload.fileName}
            </p>
            <p className="text-xs leading-5 text-slate-500 [overflow-wrap:anywhere]">
              {ext}
            </p>
          </div>
        </div>
        {error && (
          <p className="order-last min-w-0 basis-full text-xs leading-5 text-red-600 [overflow-wrap:anywhere]">
            {error}
          </p>
        )}
        {canPreview && (
          <Button
            variant="ghost"
            className="min-h-12 gap-1 px-3 text-primary hover:text-primary hover:bg-primary/5 shrink-0"
            onClick={handlePreviewToggle}
            disabled={isLoading}
          >
            {isLoading ? (
              <span className="text-xs">Loading...</span>
            ) : (
              <>
                {showPreview ? (
                  <ChevronDown size={13} className="shrink-0" />
                ) : (
                  <ChevronRight size={13} className="shrink-0" />
                )}
                <span className="text-xs">Preview</span>
              </>
            )}
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon"
          className="h-12 w-12 rounded-xl text-slate-600 hover:text-primary hover:bg-primary/5 shrink-0"
          onClick={handleDownload}
          disabled={isLoading}
          title="Download"
          aria-label={`Download ${fileUpload.fileName}`}
        >
          <Download size={13} />
        </Button>
      </div>

      {showPreview && signedUrl && (
        <div className="min-w-0 border-t border-slate-200 bg-slate-50">
          {isPdf ? (
            <PDFViewer url={signedUrl} containerClassName="h-[50vh]" />
          ) : isImage ? (
            <ImageViewer
              url={signedUrl}
              alt={fileUpload.fileName}
              containerClassName="h-[40vh]"
            />
          ) : null}
        </div>
      )}
    </div>
  );
}

export function FormResponseDialog({
  open,
  onOpenChange,
  formSubmissionId,
}: FormResponseDialogProps) {
  const trpc = useTRPC();

  const { data: submission, isLoading } = useQuery(
    trpc.organizationForm.getSubmission.queryOptions(
      { formSubmissionId },
      { enabled: open },
    ),
  );

  // Determine if this is a PDF submission by checking whether a PDF result row
  // exists. We use retry: false so a NOT_FOUND (non-PDF form) fails silently.
  const { data: pdfResult } = useQuery(
    trpc.organizationForm.getSubmissionPdfResult.queryOptions(
      { formSubmissionId },
      {
        enabled: open && !!formSubmissionId,
        retry: false,
        // Poll until filled (the only terminal fill state).
        refetchInterval: (query) => {
          const status = query.state.data?.fillStatus;
          if (status === "filled") {
            return false;
          }
          return 5000;
        },
      },
    ),
  );

  const { data: formData } = useQuery(
    trpc.organizationForm.getFormForSubmission.queryOptions(
      { formId: submission?.formId ?? "" },
      { enabled: open && !!submission?.formId },
    ),
  );

  // Build a lookup map from fieldId -> field label
  const fieldLabelMap = React.useMemo(() => {
    if (!formData) {
      return {} as Record<string, string>;
    }
    return Object.fromEntries(formData.fields.map((f) => [f.id, f.label]));
  }, [formData]);

  const submittedAt = submission?.createdAt
    ? new Date(submission.createdAt).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={FORM_RESPONSE_DIALOG_CLASS_NAME}>
        <DialogHeader className="min-w-0 border-b border-slate-200 pb-4 pr-10 text-left">
          <DialogTitle className="text-lg font-semibold leading-7 text-slate-900 [overflow-wrap:anywhere]">
            {formData?.name ?? "Form Response"}
          </DialogTitle>
          {submittedAt && (
            <p className="text-sm leading-6 text-slate-600 mt-0.5">
              Submitted {submittedAt}
            </p>
          )}
        </DialogHeader>

        {/* PDF fill result section — only shown for PDF form submissions */}
        {pdfResult && (
          <div className="min-w-0">
            <PdfResultBanner pdfResult={pdfResult} />
          </div>
        )}

        {isLoading ? (
          <div className="space-y-4 py-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="space-y-1.5">
                <div className="h-3.5 bg-slate-200 rounded w-28 animate-pulse" />
                <div className="h-12 bg-slate-100 rounded-xl animate-pulse" />
              </div>
            ))}
          </div>
        ) : !submission ? (
          <p className="text-sm text-slate-600 py-4">Submission not found.</p>
        ) : (
          <div className="min-w-0 divide-y divide-slate-200">
            {submission.values.length === 0 ? (
              <p className="text-sm text-slate-600 py-3">No values recorded.</p>
            ) : (
              submission.values.map((val) => {
                const label = fieldLabelMap[val.formFieldId] ?? "Field";
                const hasFiles =
                  val.fileAttachments && val.fileAttachments.length > 0;

                return (
                  <div
                    key={val.id}
                    className="min-w-0 py-4 first:pt-0 last:pb-0"
                  >
                    <p className="text-sm font-medium text-slate-600 mb-2 [overflow-wrap:anywhere]">
                      {label}
                    </p>
                    <div className="min-w-0 text-sm text-slate-900">
                      {val.textValue ? (
                        <p className="whitespace-pre-wrap leading-6 [overflow-wrap:anywhere]">
                          {val.textValue}
                        </p>
                      ) : hasFiles ? (
                        <div className="min-w-0 space-y-3">
                          {val.fileAttachments.map((attachment) =>
                            attachment.fileUpload ? (
                              <FileAttachmentLink
                                key={attachment.id}
                                formSubmissionId={formSubmissionId}
                                fileUpload={attachment.fileUpload}
                              />
                            ) : (
                              <Badge
                                key={attachment.id}
                                variant="outline"
                                className="min-h-7 rounded-lg text-xs bg-slate-50 text-slate-600 border-slate-200 whitespace-normal"
                              >
                                File unavailable
                              </Badge>
                            ),
                          )}
                        </div>
                      ) : (
                        <span className="text-slate-500 italic text-sm">
                          No response
                        </span>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
